import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { IdleService } from '../services/idle.service';

const SKIP_URLS = ['/auth/login', '/auth/logout', '/auth/refresh-token', '/auth/forgot-password', '/auth/reset-password'];

@Injectable()
export class UnauthorizedInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshToken$ = new BehaviorSubject<string | null>(null);

  constructor(private injector: Injector) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        const skip = SKIP_URLS.some((u) => request.url.includes(u));

        if (error.status === 401 && !skip) {
          const idle = this.injector.get(IdleService);
          // If user has been idle for more than 1 hour, don't try to refresh — just logout
          if (idle.isIdleTooLong()) {
            this.injector.get(AuthService).logout();
            return throwError(() => error);
          }
          return this.handle401(request, next);
        }

        return throwError(() => error);
      })
    );
  }

  private handle401(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const auth = this.injector.get(AuthService);

    if (this.isRefreshing) {
      // Another refresh is already in-flight — queue this request until the new token arrives
      return this.refreshToken$.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => next.handle(this.addToken(request, token!)))
      );
    }

    this.isRefreshing = true;
    this.refreshToken$.next(null);

    return auth.refreshToken().pipe(
      switchMap((res) => {
        this.isRefreshing = false;
        const newToken = res.data.accessToken;
        this.refreshToken$.next(newToken);
        return next.handle(this.addToken(request, newToken));
      }),
      catchError((err) => {
        this.isRefreshing = false;
        auth.logout();
        return throwError(() => err);
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` }, withCredentials: true });
  }
}
